import styled, { CSSObject } from 'foundations-theming/utils';
import { fwcColor } from 'foundations-theming/utils/fwc';
import { Theme } from 'foundations-theming';
import {
  StyledButton,
  StyledButtonTextContainer,
  UnstyledButton,
} from '../Button/PrivateButton';
import { StyledTag } from '../Tag/Tag';
import { CheckboxWrapper } from '../Checkbox/Checkbox';
import { SpinnerOuter } from '../LoadingSpinner/LoadingSpinner';
import {
  StyledButtonWrapper,
  StyledContentLabel,
  StyledItem,
} from '../Typeahead/TypeaheadResultsItem';
import { StyledButtonContents } from '../AbstractDropdown/AbstractDropdown';
import { ComputedAnchorType, SelectProps } from './Select.types';
import { StyledView } from '../../../components/View/View';
import { FieldsetContextValue } from '../../context/FieldsetContext';
import { StyledInput } from '../TextInput/TextInput';
import { BaseStyleProps } from '../../../types';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { StyledImage } from '../Image/Image';
import { BREAKPOINTS } from '../Grid/Grid.types';
import {
  isLinkUse,
  isFormUse,
  isButtonUse,
  isUnstyledUse,
} from '../Button/Button.helpers';
import { StyledCaret } from '../AbstractDropdown/DropdownCaret';
import { SELECT } from '../../../constants/nonTokenisedValues';
import { CSS_SPECIFICITY } from '../../constants/CSSSpecificity';
import {
  getFocusPseudoClass,
  getFocusStyleObject,
} from '../../utils/focus/focus.style';
import { DROPDOWN_MENU_HEIGHT } from '../../constants';

const smallMediaQuery = `@media only screen and (max-width: ${BREAKPOINTS.sm}px)`;

export const commonErrorStyles = (theme: Theme): CSSObject => ({
  border: `${theme.borderWidth['100']} solid ${theme.color['border-alert-default']}`,
});

/**
 * NOTE: these common styles rules (in UIComponents) came from react-select-plus css, which applied styles to these classes globally.
 *
 * in foundations-components, we use styled-components to apply these rules only to:
 *
 * 1. the react-select-plus component
 * 2. the results dropdown (options)
 * 3. collapsed multi-select anchor (which mimics the L&F of a select control by reusing the same css classes)
 *
 * in a perfect world we would retire all styles out of here by copying them out to the "native" stlyed-components rules for each of these components
 */
const commonReactSelectPlusStyles = (theme: Theme): CSSObject => ({
  // These rules are copied from https://github.com/HubSpot/react-select-plus/blob/v1.0.0-rc.3.patch12/dist/react-select-plus.css, converted to styled-components and tokenized
  // NOTE: the version repackaged as react-select-plus@1 (and the latest npm release) is `v1.0.0-rc.3.patch12`

  '.Select': {
    position: 'relative',
  },

  '.Select, .Select div, .Select input, .Select span': {
    WebkitBoxSizing: 'border-box',
    MozBoxSizing: 'border-box',
    boxSizing: 'border-box',
  },

  '.Select.is-disabled > .Select-control': {
    backgroundColor: theme.color['fill-field-disabled'],
  },

  '.Select.is-disabled > .Select-control:hover': {
    boxShadow: 'none',
  },

  '.Select.is-disabled .Select-arrow-zone': {
    cursor: 'default',
    pointerEvents: 'none',
    opacity: 0.35,
  },

  '.Select-control': {
    backgroundColor: theme.color['fill-surface-default'],
    borderColor: theme.color['border-core-default'],
    borderRadius: theme.borderRadius['100'],
    border: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`,
    color: theme.color['text-core-default'],
    cursor: 'default',
    display: 'table',
    borderSpacing: 0,
    borderCollapse: 'separate',
    height: '36px',
    outline: 'none',
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
  },

  '.Select-control:hover': {
    boxShadow: '0 1px 0 rgba(0, 0, 0, 0.06)',
  },

  '.Select-control .Select-input:focus': {
    outline: 'none',
  },

  '.is-searchable.is-open > .Select-control': {
    cursor: 'text',
  },

  '.is-open > .Select-control': {
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
    background: theme.color['fill-surface-default'],
    borderColor: theme.color['border-core-default'],
  },

  '.is-open > .Select-control .Select-arrow': {
    top: '-2px',
    borderColor: 'transparent transparent #999', // no token available for #999
    borderWidth: '0 5px 5px', // no token available for 5px
  },

  '.is-searchable.is-focused:not(.is-open) > .Select-control': {
    cursor: 'text',
  },

  '.is-focused > .Select-control': {
    ...getFocusStyleObject({ theme, unwrapped: true }),
  },

  '.Select-placeholder, .Select--single > .Select-control .Select-value': {
    bottom: 0,
    color: '#aaa', // no token available for #aaa
    left: 0,
    lineHeight: '34px',
    paddingInlineStart: theme.space['250'],
    paddingInlineEnd: theme.space['250'],
    position: 'absolute',
    right: 0,
    top: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  '.has-value.Select--single > .Select-control .Select-value .Select-value-label, .has-value.is-pseudo-focused.Select--single > .Select-control .Select-value .Select-value-label':
    {
      color: theme.color['text-core-default'],
    },

  '.has-value.Select--single > .Select-control .Select-value a.Select-value-label, .has-value.is-pseudo-focused.Select--single > .Select-control .Select-value a.Select-value-label':
    {
      cursor: 'pointer',
      textDecoration: 'none',
    },

  '.has-value.Select--single > .Select-control .Select-value a.Select-value-label:hover, .has-value.is-pseudo-focused.Select--single > .Select-control .Select-value a.Select-value-label:hover, .has-value.Select--single > .Select-control .Select-value a.Select-value-label:focus, .has-value.is-pseudo-focused.Select--single > .Select-control .Select-value a.Select-value-label:focus':
    {
      color: '#007eff', // no token available for #007eff
      outline: 'none',
      textDecoration: 'underline',
    },

  '.Select-input': {
    height: '34px',
    paddingInline: theme.space['400'],
    verticalAlign: 'middle',
  },

  '.Select-input > input': {
    width: '100%',
    background: 'none transparent',
    border: '0 none',
    boxShadow: 'none',
    cursor: 'default',
    display: 'inline-block',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    margin: 0,
    outline: 'none',
    lineHeight: '14px',
    paddingBlockStart: theme.space['200'],
    paddingBlockEnd: theme.space['300'],
    paddingInline: theme.space['0'],
    WebkitAppearance: 'none',
  },

  '.is-focused .Select-input > input': {
    cursor: 'text',
  },

  '.has-value.is-pseudo-focused .Select-input': {
    opacity: 0,
  },

  '.Select-control:not(.is-searchable) > .Select-input': {
    outline: 'none',
  },

  '.Select-loading-zone': {
    cursor: 'pointer',
    display: 'table-cell',
    position: 'relative',
    textAlign: 'center',
    verticalAlign: 'middle',
    width: '16px',
  },

  '.Select-loading': {
    WebkitAnimation: 'Select-animation-spin 400ms infinite linear',
    OAnimation: 'Select-animation-spin 400ms infinite linear',
    animation: 'Select-animation-spin 400ms infinite linear',
    width: '16px',
    height: '16px',
    boxSizing: 'border-box',
    borderRadius: '50%',
    border: `${theme.borderWidth['200']} solid ${theme.color['border-core-default']}`,
    borderRightColor: theme.color['text-core-default'],
    display: 'inline-block',
    position: 'relative',
    verticalAlign: 'middle',
  },

  '.Select-clear-zone': {
    WebkitAnimation: 'Select-animation-fadeIn 200ms',
    OAnimation: 'Select-animation-fadeIn 200ms',
    animation: 'Select-animation-fadeIn 200ms',
    color: '#999', // no token available for #999
    cursor: 'pointer',
    display: 'table-cell',
    position: 'relative',
    textAlign: 'center',
    verticalAlign: 'middle',
    width: '17px',
  },

  '.Select-clear-zone:hover': {
    color: '#D0021B', // no token available for #D0021B
  },

  '.Select-clear': {
    display: 'inline-block',
    fontSize: theme.typography['heading-500'].fontSize,
    lineHeight: 1,
  },

  '.Select--multi .Select-clear-zone': {
    width: '17px',
  },

  '.Select-arrow-zone': {
    cursor: 'pointer',
    display: 'table-cell',
    position: 'relative',
    textAlign: 'center',
    verticalAlign: 'middle',
    width: '25px',
    paddingRight: '5px', // no token available for 5px
  },

  '.Select-arrow': {
    borderColor: '#999 transparent transparent', // no token available for #999
    borderStyle: 'solid',
    borderWidth: '5px 5px 2.5px', // no token available for 5px
    display: 'inline-block',
    height: 0,
    width: 0,
    position: 'relative',
  },

  '.is-open .Select-arrow, .Select-arrow-zone:hover > .Select-arrow': {
    borderTopColor: '#666', // no token available for #666
  },

  '.Select--multi .Select-multi-value-wrapper': {
    display: 'inline-block',
  },

  '.Select .Select-aria-only': {
    position: 'absolute',
    display: 'inline-block',
    height: '1px',
    width: '1px',
    margin: theme.space['250'],
    clip: 'rect(0, 0, 0, 0)',
    overflow: 'hidden',
    float: 'left',
  },

  '@-webkit-keyframes Select-animation-fadeIn': {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },

  '@keyframes Select-animation-fadeIn': {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },

  '.Select-menu-outer': {
    borderBottomRightRadius: theme.borderRadius['100'],
    borderBottomLeftRadius: theme.borderRadius['100'],
    backgroundColor: theme.color['fill-surface-default'],
    border: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`,
    borderTopColor: '#e6e6e6',
    boxShadow: '0 1px 0 rgba(0, 0, 0, 0.06)',
    boxSizing: 'border-box',
    marginTop: '-1px',
    maxHeight: '200px',
    position: 'absolute',
    top: '100%',
    width: '100%',
    zIndex: 1,
    WebkitOverflowScrolling: 'touch',
  },

  '.Select-menu': {
    maxHeight: '198px',
    overflowY: 'auto',
  },

  '.Select-option-group-label': {
    boxSizing: 'border-box',
    backgroundColor: theme.color['fill-surface-default'],
    color: theme.color['text-core-default'],
    fontWeight: theme.typography['heading-50'].fontWeight,
    cursor: 'default',
    display: 'block',
    paddingInline: theme.space['250'],
    paddingBlock: theme.space['200'],
  },

  '.Select-option-group-label ~ .Select-option, .Select-option-group-label ~ .Select-option-group':
    {
      paddingInlineStart: theme.space['500'],
    },

  '.Select-option': {
    boxSizing: 'border-box',
    backgroundColor: theme.color['fill-surface-default'],
    color: theme.color['text-core-default'],
    cursor: 'pointer',
    display: 'block',
    paddingInline: theme.space['250'],
    paddingBlock: theme.space['200'],
  },

  '.Select-option:last-child': {
    borderBottomRightRadius: theme.borderRadius['100'],
    borderBottomLeftRadius: theme.borderRadius['100'],
  },

  '.Select-option.is-selected': {
    backgroundColor: 'inherit',
    color: 'inherit',
  },

  '.Select-option.is-focused': {
    backgroundColor: theme.color['fill-field-hover'],
    color: theme.color['text-core-default'],
  },

  '.Select-option.is-disabled': {
    color: theme.color['text-core-disabled'],
    cursor: 'default',
  },

  '.Select-noresults': {
    boxSizing: 'border-box',
    color: theme.color['text-secondary-disabled'],
    cursor: 'default',
    display: 'block',
    paddingInline: theme.space['250'],
    paddingBlock: theme.space['200'],
  },

  '.Select--multi .Select-input': {
    verticalAlign: 'middle',
    marginInlineStart: theme.space['0'],
    paddingInline: theme.space['0'],
  },

  '.Select--multi.has-value .Select-input': {
    marginLeft: '5px',
  },

  '.Select--multi .Select-value': {
    backgroundColor: '#ebf5ff',
    borderRadius: '2px',
    border: '1px solid #c2e0ff',
    color: '#007eff', // no token available for #007eff
    display: 'inline-block',
    fontSize: '0.9em',
    lineHeight: '1.4',
    marginLeft: '5px',
    marginTop: '5px',
    verticalAlign: 'top',
  },

  '.Select--multi .Select-value-icon, .Select--multi .Select-value-label': {
    display: 'inline-block',
    verticalAlign: 'middle',
  },

  '.Select--multi .Select-value-label': {
    borderBottomRightRadius: '2px',
    borderTopRightRadius: '2px',
    cursor: 'default',
    padding: '2px 5px',
  },

  '.Select--multi a.Select-value-label': {
    color: '#007eff', // no token available for #007eff
    cursor: 'pointer',
    textDecoration: 'none',
  },

  '.Select--multi a.Select-value-label:hover': {
    textDecoration: 'underline',
  },

  '.Select--multi .Select-value-icon': {
    cursor: 'pointer',
    borderBottomLeftRadius: '2px',
    borderTopLeftRadius: '2px',
    borderRight: '1px solid #c2e0ff',
    padding: '1px 5px 3px',
  },

  '.Select--multi .Select-value-icon:hover, .Select--multi .Select-value-icon:focus':
    {
      backgroundColor: '#d8eafd',
      color: '#0071e6', // no token available for #0071e6
    },

  '.Select--multi .Select-value-icon:active': {
    backgroundColor: '#c2e0ff',
  },

  '.Select--multi.is-disabled .Select-value': {
    backgroundColor: '#fcfcfc',
    border: '1px solid #e3e3e3',
    color: theme.color['text-core-default'],
  },

  '.Select--multi.is-disabled .Select-value-icon': {
    cursor: 'not-allowed',
    borderRight: '1px solid #e3e3e3',
  },

  '.Select--multi.is-disabled .Select-value-icon:hover, .Select--multi.is-disabled .Select-value-icon:focus, .Select--multi.is-disabled .Select-value-icon:active':
    {
      backgroundColor: '#fcfcfc',
    },

  '@keyframes Select-animation-spin': {
    to: {
      transform: 'rotate(1turn)',
    },
  },

  '@-webkit-keyframes Select-animation-spin': {
    to: {
      WebkitTransform: 'rotate(1turn)',
    },
  },
});

const commonSelectControlStyles = (theme: Theme): CSSObject => ({
  borderColor: theme.color['border-core-default'],
  backgroundColor: theme.color['fill-surface-raised'],
  borderCollapse: 'separate',
  borderRadius: theme.borderRadius['100'],
  borderSpacing: 0,
  border: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`,
  color: theme.color['text-core-default'],
  display: 'flex',
  lineHeight: '22px',
  width: '100%',
  alignItems: 'center',
  userSelect: 'none' as const,
  cursor: 'inherit',
  height: 'auto',
  minHeight: '40px',
  verticalAlign: 'middle',
  textAlign: 'left' as const,
  fontSize: theme.typography['body-200'].fontSize,
  outline: 'none',
  overflow: 'hidden',
  position: 'relative',
});

const commonOptionStyles = (theme: Theme): CSSObject => ({
  padding: theme.space['0'],

  // don't apply to ColorSwatch buttons
  [`& ${UnstyledButton}:not([data-color-swatch])`]: {
    border: 0,
    borderRadius: theme.borderRadius['0'],
    color: theme.color['text-core-default'],
    display: 'block',
    minHeight: '40px',
    paddingInline: theme.space['400'],
    paddingBlock: theme.space['200'],
    textAlign: 'left' as const,
    width: '100%',
    fontSize: theme.typography['body-125'].fontSize,
    fontWeight: theme.typography['body-300'].fontWeight,
  },

  [`& ${StyledButtonTextContainer}:not(:has([data-loading="true"]))`]: {
    justifyContent: 'flex-start',
  },

  [`& ${CheckboxWrapper}`]: {
    minHeight: '40px',
  },

  '&:last-child': {
    borderRadius: theme.borderRadius['0'],
  },

  '&.is-selected': {
    backgroundColor: 'inherit',
    color: 'inherit',
  },

  '&.is-focused': {
    background: theme.color['fill-field-hover'],
    color: theme.color['text-core-default'],
  },

  '&.is-disabled': {
    [`& ${StyledButtonWrapper} > ${UnstyledButton}`]: {
      backgroundColor: `${theme.color['fill-surface-default']} !important`,
      borderColor: `${theme.color['border-tertiary-disabled']} !important`,
      color: `${theme.color['text-core-disabled']} !important`,
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  },
});

export const StyledTypeaheadResultsSpan = styled.span(({ theme }) => {
  return {
    WebkitTapHighlightColor: theme.color['specialty-scrim'],
    borderRadius: 'inherit',
    listStyle: 'none',
    margin: theme.space['0'],
    maxHeight: DROPDOWN_MENU_HEIGHT,
    overflowX: 'hidden',
    overflowY: 'auto',
    // iOS Safari requires these for touch scrolling to work inside a positioned container;
    // without them the page scrolls instead of the dropdown list.
    WebkitOverflowScrolling: 'touch',
    touchAction: 'pan-y',
    overscrollBehavior: 'contain',
    padding: theme.space['0'],
    position: 'relative',
    '& [role="listbox"]': {
      [getFocusPseudoClass()]: {
        ...getFocusStyleObject({ theme, unwrapped: true }),
      },
    },
  };
});

export const StyledMultiValueSpan = styled.span<{
  $hasValueTags: boolean;
}>(({ theme, $hasValueTags }) => {
  return {
    maxWidth: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    ...($hasValueTags && { marginBlockStart: '-9px' }),

    [`& ${StyledTag}`]: {
      marginBlockStart: '7px',
      '&:not(:last-child)': {
        marginInlineEnd: '5px',
      },
      [`& + ${StyledTag}`]: {
        marginInlineStart: theme.space['0'],
      },
    },
  };
});

export const HiddenDiv = styled.div<{
  $open?: boolean;
}>(({ $open }) => {
  return {
    [CSS_SPECIFICITY.OVERRIDE]: {
      position: 'absolute',
      visibility: $open ? 'visible' : 'hidden',
      height: 0,
      width: 0,
      overflow: 'hidden',
    },
  };
});

export const StyledDropdownFooterContainer = styled.div.attrs({
  'data-dropdown-footer': true,
})(({ theme }) => {
  return {
    paddingBlock: theme.space['400'],
    paddingInline: theme.space['500'],
    borderBlockStart: `${theme.borderWidth['100']} solid ${theme.color['border-transitional-core-subtle']}`,
  };
});

export const StyledSelectArrow = styled.div(({ theme }) => {
  return {
    display: 'inline-block',
    verticalAlign: 'middle',
    width: 0,
    height: 0,
    borderStyle: 'solid',
    borderWidth: SELECT.BORDER_WIDTH,
    borderColor: theme.color['fill-transparent-default'],
    borderTopColor: theme.color['border-info-default'],
    marginBlockStart: SELECT.MARGIN_BLOCK_START,
  };
});

export const StyledMultiValueWrapper = styled.div(({ theme }) => {
  return {
    marginBlockEnd: `-${theme.space['25']}`,
    marginBlockStart: `-${theme.space['25']}`,
    minHeight: '40px',
    width: 'calc(100% - 33px)',
  };
});

export const StyledArrowZone = styled.div<{ $multi: boolean }>(
  ({ theme, $multi }) => {
    return {
      [CSS_SPECIFICITY.OVERRIDE]: {
        display: 'table-cell',
        marginInlineStart: $multi ? '5px' : theme.space['400'], // no token for 5px
        width: $multi ? '28px' : 'auto',
        paddingInlineEnd: $multi ? theme.space['400'] : theme.space['0'],
      },
    };
  }
);

export const StyledCollapsedAnchorSelectControl = styled.div<{
  $multi: boolean;
}>(({ theme, $multi }) => {
  return {
    [CSS_SPECIFICITY.OVERRIDE]: {
      ...commonSelectControlStyles(theme),
      ...($multi && {
        flexWrap: 'wrap',

        '& .has-value-tags': {
          marginBlockStart: '-9px',
        },

        '& .Select-multi-value-wrapper': {
          paddingInlineStart: theme.space['400'],
          paddingInlineEnd: theme.space['200'],
        },

        [`& ${StyledTag}`]: {
          display: 'inline-flex',
          flexShrink: '0',
          marginInlineStart: theme.space['0'],
          marginInlineEnd: theme.space['200'],
          marginBlockStart: theme.space['200'],
          maxWidth: 'calc(100% - 16px)',
          verticalAlign: 'top',

          '&:last-of-type': {
            marginBlockEnd: theme.space['200'],
          },

          '& .Select-arrow-zone, & .Select-clear-zone': {
            paddingInlineEnd: `${theme.space['400']} !important`,
            width: $multi ? '28px' : 'auto',
            display: $multi ? 'table-cell' : '',
            marginInlineStart: $multi ? '5px' : '',
          },
        },
      }),

      '& .Select-arrow-zone > .Select-arrow': {
        borderColor: theme.color['fill-transparent-default'],
        borderStyle: 'solid',
        borderWidth: SELECT.BORDER_WIDTH,
        display: 'inline-block',
        verticalAlign: 'middle',
        marginBlockStart: SELECT.MARGIN_BLOCK_START,
        borderBlockStartColor: theme.color['icon-interactive-default'],
        top: '0',
      },

      // apply same error styles as when anchorType='input' (TextInput) when collapsed
      '&[aria-invalid="true"]': {
        ...commonErrorStyles(theme),
      },
    },
  };
});

export const StyledReactSelectPlusWrapper = styled.div<{
  $cssStyles?: CSSObject;
  $multi: boolean;
  $anchorType: ComputedAnchorType;
  $buttonUse?: SelectProps['buttonUse'];
  $isDisplayOptimized?: boolean;
  $fieldSize?: FieldsetContextValue['size'];
  $disabled?: boolean;
  $readOnly?: boolean;
  $error?: boolean;
  $placeholder?: boolean;
}>(
  ({
    theme,
    $multi,
    $cssStyles,
    $anchorType,
    $buttonUse,
    $isDisplayOptimized,
    $fieldSize,
    $disabled,
    $readOnly,
    $error,
    $placeholder,
  }) => {
    return {
      [CSS_SPECIFICITY.BASIC]: {
        ...commonReactSelectPlusStyles(theme),
      },
      [CSS_SPECIFICITY.OVERRIDE]: {
        maxWidth: '100%',
        cursor: 'pointer',
        // NOTE: we would prefer `display: contents` if it were supported by all
        // browsers (specifically, Edge 18 and Safari 11). That would allow flex
        // containers to grow the contents, and the contents to be responsive without
        // the wrapper interfering. Instead use flex styling to stretch contents
        // so they have the same width as the wrapper
        // See https://caniuse.com/css-display-contents
        // See https://product.hubteam.com/docs/frontend/docs/browser-support.html
        // FIXME: link display should be 'inline', not 'inline-block' (`.private-button__link`) so text content can wrap. however this needs to be fixed in `Button` as well before it will work
        display:
          $anchorType === 'button' &&
          $buttonUse &&
          (isButtonUse($buttonUse) || isLinkUse($buttonUse))
            ? 'inline-block'
            : 'block',

        ...($anchorType === 'button' &&
          $buttonUse && {
            ...(isButtonUse($buttonUse) && {
              // make child button size match wrapper size, so styles sizing the wrapper size the button as well
              // expanding the wrapper will expand the button
              // NOTE: see note above about `display: contents`
              [`& > ${StyledButton}`]: {
                width: '100%',
                height: '100%',
              },

              // make button wrapper width responsive to mimic `.private-button--default`
              // NOTE: see note above about `display: contents`
              [smallMediaQuery]: {
                width: '100%',
              },
            }),

            ...((isLinkUse($buttonUse) || isUnstyledUse($buttonUse)) && {
              ...($error && {
                [`& > [role="button"][aria-invalid="true"], & > ${StyledButton}[aria-invalid="true"]`]:
                  {
                    ...commonErrorStyles(theme),
                  },
              }),
            }),
          }),

        // make form input wrapper full-width to mimic `.private-form-control`
        // NOTE: see note above about `display: contents`
        ...(($anchorType === 'combined' ||
          ($anchorType === 'button' &&
            $buttonUse &&
            isFormUse($buttonUse))) && {
          width: '100%',
        }),

        // override responsive button styles which center content on narrow screens.
        // without this, multi button value tags get centered, instead of staying flush left like a native input
        [`& ${StyledButtonTextContainer}`]: {
          ...($multi && { alignItems: 'flex-start' }),
          [smallMediaQuery]: {
            span: {
              justifyContent: 'flex-start',
            },
          },
        },

        ...($buttonUse &&
          isFormUse($buttonUse) && {
            [`& ${StyledButton}`]: {
              paddingInlineStart: $isDisplayOptimized
                ? theme.space['0']
                : theme.space['400'],
              paddingInlineEnd: theme.space['400'],
              paddingBlock: $isDisplayOptimized
                ? $multi
                  ? theme.space['100']
                  : theme.space['0']
                : theme.space['200'],

              // Compact version
              ...($fieldSize === 'small' && {
                paddingBlock: theme.space['100'],
                fontSize: theme.typography['body-100'].fontSize,
                ...(!$multi && { blockSize: '32px' }),
              }),

              // Disabled state
              ...($disabled && {
                borderColor: `${theme.color['border-tertiary-disabled']} !important`,
              }),

              // Read-only state
              ...($readOnly && {
                backgroundColor: $isDisplayOptimized
                  ? theme.color['fill-transparent-default']
                  : `${
                      theme.color[fwcColor('fwc-textinput-fill-readonly')]
                    } !important`,
                borderColor: `${
                  theme.color[fwcColor('fwc-textinput-border-readonly')]
                } !important`,
                color: `${theme.color['text-core-subtle']} !important`,
                cursor: 'auto !important',
                '& *, & [data-typeahead-item], & [data-option-text], & span': {
                  cursor: 'auto !important',
                },
              }),

              // Label content
              [`& ${StyledContentLabel}`]: {
                lineHeight: '22px',
              },

              // Button contents
              [`& ${StyledButtonContents}`]: {
                lineHeight: '22px',
                // make sure svgs inside button have the same height as text so that the button stays same height even when there's no text
                'svg[data-icon-name]': {
                  blockSize: '22px',
                },
              },
            },

            // Error state
            // when multi=true and anchorType=combined (buttonUse=form), the reference element (fnd-select) is not inside a StyledButton but rather is a direct child of StyledReactSelectPlusWrapper
            ...($error && {
              '& .fnd-select': {
                ...commonErrorStyles(theme),
                borderRadius: theme.borderRadius['100'],
                '& .Select-control': {
                  border: 'none',
                },
              },
            }),
          }),

        '& .Select-control': commonSelectControlStyles(theme),

        //TODOVAR: add styles for maxHeight

        // Handle all Select/Button combinations
        '& + button, & + [class*="StyledReactSelectPlusWrapper"], button + &': {
          marginInlineStart: theme.space['300'],
        },

        '& .is-open': {
          cursor: 'text',
        },

        '&.fnd-select:not(.has-value)': {
          [`& ${StyledContentLabel}, & ${StyledMultiValueSpan}`]: {
            // Placeholder styles
            ...($placeholder && {
              color: theme.color['text-core-subtle'],
            }),
          },
        },

        '& .Select-placeholder, &:not(.Select--multi) .Select-control .Select-value':
          {
            color: 'inherit',
            lineHeight: 'inherit',
            right: '32px',
            paddingBlock: '9px',
            paddingInline: theme.space['400'],
          },

        '& .Select--single': {
          '& .Select-value': {
            position: 'relative',
          },

          '& .Select-value + .Select-input': {
            left: '0',
            opacity: '0',
            position: 'absolute',
            top: '0',
          },

          [`& ${UnstyledButton}`]: {
            cursor: 'pointer',
          },
        },

        '& .Select-option': commonOptionStyles(theme),

        '& .Select--multi .Select-multi-value-wrapper': {
          paddingInlineStart: theme.space['400'],
          paddingInlineEnd: theme.space['200'],
        },

        '& .Select-multi-value-wrapper': {
          marginBlockEnd: `-${theme.space['25']}`,
          marginBlockStart: `-${theme.space['25']}`,
          minHeight: '40px',
          width: 'calc(100% - 33px)',

          [`& ${StyledTag}`]: {
            display: 'inline-flex',
            flexShrink: '0',
            marginInlineStart: theme.space['0'],
            marginInlineEnd: theme.space['200'],
            marginTop: theme.space['200'],
            maxWidth: 'calc(100% - 16px)',
            verticalAlign: 'top',

            '&:last-of-type': {
              marginBlockEnd: theme.space['200'],
            },
          },
        },

        '& .Select-input': {
          flex: '1 1 auto',
          height: 'auto',

          '& > input': {
            paddingBlock: '9px',
            paddingInline: theme.space['250'],
            cursor: 'text',
            height: 'auto',
            lineHeight: '22px',
            paddingLeft: '0',

            '&:-moz-focusring': {
              outline: '0',
            },
          },
        },

        '& .Select-clear-zone': {
          '&:hover': {
            color: 'inherit',
          },
        },

        '& .Select-clear': {
          lineHeight: 'inherit',
        },

        '& .Select-arrow-zone, & .Select-clear-zone': {
          paddingInlineEnd: `${theme.space['400']} !important`,
          width: $multi ? '28px' : 'auto',
          display: $multi ? 'table-cell' : '',
          marginInlineStart: $multi ? '5px' : '',
        },

        '& .Select-arrow-zone > .Select-arrow': {
          borderColor: theme.color['fill-transparent-default'],
          borderStyle: 'solid',
          borderWidth: SELECT.BORDER_WIDTH,
          display: 'inline-block',
          verticalAlign: 'middle',
          marginBlockStart: SELECT.MARGIN_BLOCK_START,
          borderBlockStartColor: theme.color['icon-interactive-default'],
          top: '0',
        },

        '& .Select:not(.is-open)': {
          '& .Select-input': {
            cursor: 'pointer',
          },
        },

        '&.has-value:not(.Select--multi) > .Select-control .Select-value .Select-value-label':
          {
            color: theme.color['fill-accent-neutral-pressed'],
          },

        '&.is-disabled': {
          '&:not(.is-readonly) > .Select-control': {
            [`& ${UnstyledButton}`]: {
              backgroundColor: `${theme.color['fill-surface-default']} !important`,
              borderColor: `${theme.color['border-secondary-disabled']} !important`,
              color: `${theme.color['text-secondary-disabled']} !important`,
              cursor: 'not-allowed',
            },

            '& .Select-arrow-zone > .Select-arrow': {
              borderTopColor: theme.color['fill-accent-neutral-subtle-alt'],
            },

            '& .Select-value-label': {
              color: `${theme.color['text-core-disabled']} !important`,
            },
          },
        },

        ...($disabled && {
          [`& ${StyledCaret}, & .Select-arrow`]: {
            borderTopColor: `${theme.color['icon-core-disabled']} !important`,
          },

          '.Select-control': {
            backgroundColor: `${theme.color['fill-field-disabled']} !important`,
            borderColor: `${theme.color['border-tertiary-disabled']} !important`,
            color: `${theme.color['do-not-use-button-disabled-text']} !important`,
            cursor: 'not-allowed',
          },
        }),

        ...($readOnly && {
          '.Select-control': {
            backgroundColor: `${
              theme.color[fwcColor('fwc-textinput-fill-readonly')]
            } !important`,
            borderColor: `${
              theme.color[fwcColor('fwc-textinput-border-readonly')]
            } !important`,
            color: `${theme.color['text-core-subtle']} !important`,
            cursor: 'auto !important',
          },

          '& .Select-arrow': {
            borderTopColor: `${theme.color['fill-accent-neutral-subtle-alt']} !important`,
            cursor: 'default',
            pointerEvents: 'none',
            opacity: 0.35,
          },
        }),

        '&.is-readonly > .Select-control': {
          [`& ${UnstyledButton}`]: {
            backgroundColor: `${theme.color['fill-surface-default']} !important`,
            borderColor: `${theme.color['border-secondary-disabled']} !important`,
            color: `${theme.color['text-secondary-disabled']} !important`,
            cursor: 'not-allowed',
          },

          '& .Select-arrow-zone > .Select-arrow': {
            display: 'none',
          },
        },
        ...($isDisplayOptimized && {
          [`& ${StyledView}`]: {
            marginInline: theme.space['100'],
          },
        }),

        [`& ${StyledImage}:not(${StyledView} ${StyledImage})`]: {
          marginInlineStart: theme.space['0'],
          marginInlineEnd: theme.space['300'],
        },

        [`& ${StyledItem}`]: {
          backgroundColor: 'inherit',
          borderColor: 'inherit',
          color: 'inherit',
        },

        ...$cssStyles,
      },
    };
  }
);

export const StyledDropdownResults = styled.div<{
  $isVirtualized?: boolean;
  $searchable?: boolean;
  $cssStyles?: CSSObject;
  $disabled?: boolean;
  $menuHeight?: number;
  $anchorType: ComputedAnchorType;
  $menuStyles?: BaseStyleProps;
  $hasSearchbox?: boolean;
}>(
  ({
    theme,
    $isVirtualized,
    $searchable,
    $anchorType,
    $menuHeight,
    $cssStyles,
    $disabled,
    $menuStyles,
    $hasSearchbox,
  }) => {
    const menuCSSStyles = useMapTokenPropsToCSS($menuStyles ?? {});
    return {
      [CSS_SPECIFICITY.BASIC]: {
        ...commonReactSelectPlusStyles(theme),
      },
      [CSS_SPECIFICITY.OVERRIDE]: {
        borderRadius: 'inherit',
        ...($menuHeight && {
          maxHeight: `${$menuHeight}px`,
        }),
        // Select container
        '& > div': {
          borderRadius: 'inherit',
        },

        // Menu outer wrapper
        '& .Select-menu-outer': {
          border: 0,
          borderRadius: 'inherit',
          boxShadow: 'none',
          marginTop: theme.space['0'],
          maxHeight: 'none',
          position: 'relative',
          padding: `${theme.space['0']} !important`,
        },

        ...($disabled && {
          [`& ${StyledInput}`]: {
            backgroundColor: `${theme.color['fill-primary-disabled']} !important`,
            borderColor: `${theme.color['border-tertiary-disabled']} !important`,
            color: `${theme.color['fill-tertiary-pressed']} !important`,
            cursor: 'not-allowed',
          },
          '& svg': {
            fill: `${theme.color['icon-primary-disabled']} !important`,
          },
        }),

        // Menu
        '& .Select-menu': {
          borderBottomLeftRadius: 'inherit',
          borderBottomRightRadius: 'inherit',
          // we only want to apply the top radius if there's no searchbox because
          // the searchbox will have the top radius itself and sit on top of the menu
          ...(!$hasSearchbox && {
            borderTopLeftRadius: 'inherit',
            borderTopRightRadius: 'inherit',
          }),
          maxHeight: $menuHeight
            ? `${$menuHeight}px`
            : $isVirtualized ||
              ($anchorType !== 'input' &&
                $anchorType !== 'combined' &&
                $searchable)
            ? DROPDOWN_MENU_HEIGHT
            : '228px',
          paddingBottom:
            $isVirtualized ||
            ($anchorType !== 'input' &&
              $anchorType !== 'combined' &&
              $searchable)
              ? theme.space['0']
              : theme.space['200'],
          paddingTop:
            $isVirtualized ||
            ($anchorType !== 'input' &&
              $anchorType !== 'combined' &&
              $searchable)
              ? theme.space['0']
              : theme.space['200'],
          overflowY: 'auto',
          overflowX: $isVirtualized ? 'hidden' : 'auto',
          // iOS Safari requires these for touch scrolling to work inside a positioned container
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          overscrollBehavior: 'contain',
          ...menuCSSStyles,
        },

        [`& ${SpinnerOuter}`]: {
          marginInline: 'auto !important',
          transform: 'scale(1) !important', // Override default 0.75 scale to show spinner at normal size
        },

        // Options
        '& .Select-option': commonOptionStyles(theme),

        // Option group label
        '& .Select-option-group-label': {
          margin: 0,
          minHeight: '40px',
          color: theme.color['text-core-default'],
          padding: '4px 0 0',
          [`& ${UnstyledButton}`]: {
            fontWeight: theme.typography['heading-50'].fontWeight,
            fontSize: theme.typography['heading-50'].fontSize,
            display: 'block',
            lineHeight: 'normal',
            textTransform: 'none',
            cursor: 'default',
            color: 'inherit',
            '&:hover': {
              background: theme.color['fill-transparent-default'],
            },
          },
        },

        // No results
        '& .Select-noresults': {
          opacity: 0.5,
          backgroundColor: `${theme.color['fill-surface-default']} !important`,
          borderColor: `${theme.color['border-secondary-disabled']} !important`,
          color: `${theme.color['text-secondary-disabled']} !important`,
          cursor: 'not-allowed',
        },

        // Tags
        [`& ${StyledTag}`]: {
          cursor: 'inherit',
        },

        /* Base padding for options */
        '& .Select-option-group': {
          // Ensure option groups themselves have no padding
          paddingInlineStart: `${theme.space['0']}`,
          padding: `${theme.space['0']}`,

          '& .Select-option-group-label': {
            paddingInlineStart: `${theme.space['0']}`,
          },

          '& .Select-option': {
            paddingInlineStart: `${theme.space['500']}`, // 20px
          },

          /* First level nesting */
          '& .Select-option-group': {
            '& .Select-option-group-label': {
              paddingInlineStart: `${theme.space['500']}`,
            },
            '& .Select-option': {
              paddingInlineStart: `${theme.space['1000']}`, // 40px
            },

            /* Second level nesting */
            '& .Select-option-group': {
              '& .Select-option-group-label': {
                paddingInlineStart: `${theme.space['1000']}`,
              },

              '& .Select-option': {
                paddingInlineStart: `${theme.space['1300']}`, // 60px
              },

              /* Third level nesting */
              '& .Select-option-group': {
                '& .Select-option-group-label': {
                  paddingInlineStart: `${theme.space['1300']}`,
                },

                '& .Select-option': {
                  paddingInlineStart: '80px',
                },

                /* Fourth level nesting */
                '& .Select-option-group': {
                  '& .Select-option-group-label': {
                    paddingInlineStart: '80px',
                  },

                  '& .Select-option': {
                    paddingInlineStart: '100px',
                  },
                },
              },
            },
          },
        },

        ...$cssStyles,
      },
    };
  }
);
